import { usePrevious } from '@react-hookz/web';
import { useBreakpointDown } from '@travel-frontend/core';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import * as ActionTypes from '../../../../../components/SearchForm/components/TravelPeriodField/actionTypes.js';
import { useWidgetOverlay } from '../../../../../components/SearchForm/hooks/useWidgetOverlay.js';
import { getTravelPeriod } from '../../../../../components/SearchForm/redux/reducer.js';
export const useTravelPeriodFieldNew = ({
  value: initialValue,
  config: {
    required,
    defaultDuration,
    minDate,
    maxDate,
    durationOptions,
    durationOptionsNew,
    holidayDates,
    showHolidays,
    durationLimit
  },
  floatingOptions,
  invalid = false,
  disabled = false,
  dimming = false,
  layout = 'full',
  dispatch
}) => {
  const mdDown = useBreakpointDown('md');
  const [travelPeriod, setTravelPeriod] = useState(initialValue);
  const widgetType = mdDown ? 'drawer' : 'popover';
  const [widgetOpen, setWidgetOpen] = useState(false);
  const previousDefaultDuration = usePrevious(defaultDuration);
  const period = useSelector(getTravelPeriod);
  const isDefaultDurationEqual = isEqual(defaultDuration, previousDefaultDuration);
  // update default duration on change package tour or own arrival
  useEffect(() => {
    if (period) {
      setTravelPeriod(initialValue);
    } else {
      setTravelPeriod({
        ...travelPeriod,
        duration: defaultDuration
      });
    }
  }, [isDefaultDurationEqual]);
  const handleApply = (selectedState = null, type = null) => {
    const maxDuration = Math.max(travelPeriod?.duration?.max ?? 0, travelPeriod?.duration?.min ?? 0);
    const minDuration = Math.min(travelPeriod?.duration?.max ?? 0, travelPeriod?.duration?.min ?? 0);
    dispatchEvent({
      type: ActionTypes.ValueChanged,
      value: {
        ...travelPeriod,
        duration: {
          max: maxDuration,
          min: minDuration
        }
      },
      context: {
        type,
        selectedState
      }
    });
    setWidgetOpen(previous => !previous);
  };
  const toggleWidget = open => {
    setWidgetOpen(() => open);
  };
  const handleClose = () => {
    dispatchEvent({
      type: ActionTypes.CloseClicked,
      value: null
    });
    toggleWidget(false);
  };
  const handleOpenChange = open => {
    if (!open && open !== widgetOpen) {
      handleApply();
    }
    toggleWidget(open);
  };
  const overlay = useWidgetOverlay({
    open: widgetOpen,
    type: widgetType,
    options: floatingOptions,
    dimming,
    onOpenChange: handleOpenChange,
    layout
  });
  const handleDurationChange = duration => {
    const newValue = {
      ...travelPeriod,
      duration
    };
    setTravelPeriod(newValue);
    dispatchEvent({
      type: ActionTypes.DurationChanged,
      value: newValue
    });
  };
  const handleRangeChange = range => {
    const newValue = {
      ...range,
      duration: range.duration
    };
    setTravelPeriod(newValue);
    dispatchEvent({
      type: ActionTypes.RangeChanged,
      value: newValue
    });
  };
  const isValidValue = value => value !== null;
  const dispatchEvent = ({
    type,
    value,
    context
  }) => {
    let isDefaultPrevented = false;
    dispatch({
      type,
      payload: {
        value,
        valid: isValidValue(value),
        context
      },
      preventDefault: () => {
        isDefaultPrevented = true;
      }
    });
    return {
      isDefaultPrevented
    };
  };
  const getFieldProps = () => ({
    ...overlay.getReferenceProps(),
    value: travelPeriod,
    active: widgetOpen,
    defaultDuration,
    invalid,
    disabled
  });
  const getWidgetProps = () => ({
    ...overlay.getWidgetProps(),
    type: widgetType,
    // CAN I REMOVE IT?
    open: widgetOpen,
    value: travelPeriod,
    travelPeriod,
    handlers: {
      onApply: handleApply,
      onClose: handleClose,
      onDurationChange: handleDurationChange,
      onRangeChange: handleRangeChange
    },
    config: {
      defaultDuration,
      minDate,
      maxDate,
      durationOptions,
      durationOptionsNew,
      holidayDates,
      showHolidays,
      durationLimit
    },
    dimming,
    layout
  });
  const setValueHandler = nextValue => {
    dispatchEvent({
      type: ActionTypes.FunctionValueChanged,
      value: nextValue
    });
    setTravelPeriod(nextValue);
  };
  const checkValidity = () => required === false || isValidValue(travelPeriod);
  return {
    isActive: widgetOpen,
    isInvalid: invalid,
    isDisabled: disabled,
    getFieldProps,
    getWidgetProps,
    setValue: setValueHandler,
    checkValidity
  };
};